import React from 'react';
import { ChevronDown, ChevronRight, Users, Search, Phone, PhoneOff, FileText } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { formatMontant, formatNumber } from './recouvrementUtils';

// Section depliable + tableau a en-tete colore + pagination (patterns MaCaisse).
const DossiersSection = ({
  isExpanded, onToggleSection,
  eleves, filteredEleves, search, setSearch,
  currentPage, totalPages, setCurrentPage, itemsPerPage
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const pageEleves = filteredEleves.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="px-6">
      <Card>
        <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors" onClick={onToggleSection}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isExpanded ? (
                <ChevronDown size={24} className="text-gray-600" />
              ) : (
                <ChevronRight size={24} className="text-gray-600" />
              )}
              <div>
                <CardTitle className="text-xl text-purple-600 flex items-center gap-2">
                  <Users size={24} />
                  Dossiers à suivre
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  élèves dont la scolarité reste due sur l'année de la créance
                </p>
              </div>
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-6">
            <div className="border-2 border-purple-200 rounded-lg bg-purple-50/30">
              <div className="p-4 bg-purple-50 border-b border-purple-200">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-purple-900 flex items-center gap-2">
                      <Users size={20} />
                      Élèves à relancer
                    </h3>
                    <p className="text-sm text-purple-700 mt-1">
                      Les paiements restent rattachés à l'année de la créance
                    </p>
                  </div>
                  <div className="relative w-full md:max-w-xs">
                    <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      type="text"
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder="Rechercher un élève, un parent..."
                      className="pl-10 bg-white"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4">
                {filteredEleves.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-purple-200">
                    <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600 font-medium">
                      {eleves.length === 0 ? 'Aucun dossier à recouvrer' : 'Aucun dossier ne correspond à la recherche'}
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                      {eleves.length === 0
                        ? 'Les élèves ayant une scolarité impayée apparaîtront ici'
                        : 'Essayez de modifier votre recherche'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-purple-100 border-b border-purple-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-purple-900 uppercase">Élève</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-purple-900 uppercase">Parent</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-purple-900 uppercase">Mois impayés</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-purple-900 uppercase">Reste à recouvrer</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-purple-100">
                        {pageEleves.map((eleve) => {
                          const moisImpayes = eleve.mois_impayes || [];
                          return (
                            <tr key={eleve.eleve_id} className="hover:bg-purple-50 transition-colors">
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium text-gray-900">{eleve.prenom} {eleve.nom}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    {eleve.classe_nom}
                                  </span>
                                  <span className="text-xs text-gray-500">{eleve.matricule || 'Sans matricule'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-gray-800">{eleve.parent_nom || '—'}</p>
                                {eleve.parent_telephone ? (
                                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                    <Phone size={12} />
                                    {eleve.parent_telephone}
                                  </p>
                                ) : (
                                  <p className="text-xs text-orange-600 flex items-center gap-1 mt-1">
                                    <PhoneOff size={12} />
                                    Téléphone non renseigné
                                  </p>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-1">
                                  {moisImpayes.slice(0, 3).map((mois) => (
                                    <span
                                      key={mois}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800"
                                    >
                                      {mois}
                                    </span>
                                  ))}
                                  {moisImpayes.length > 3 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                      +{moisImpayes.length - 3}
                                    </span>
                                  )}
                                  {moisImpayes.length === 0 && <span className="text-sm text-gray-400">—</span>}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <p className="text-sm font-bold text-[#c66d59]">{formatMontant(eleve.reste_a_recouvrer)}</p>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between border-t pt-4">
                    <p className="text-sm text-gray-600">
                      Page {currentPage} sur {totalPages} • {formatNumber(filteredEleves.length)} dossier(s)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Précédent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Suivant
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default DossiersSection;
